import {
  Box,
  Button,
  ButtonProps,
  InputAdornment,
  Menu,
  MenuItem,
  MenuProps,
  TextField,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OptionLabel } from './option-label';
import { IconRenderer } from '../utils/render-icon';

type AllowedOptionValues = string | number | boolean;

export type TSelectOption<TValue extends AllowedOptionValues = AllowedOptionValues> = {
  value: TValue;
  label: React.ReactNode;
  icon?: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  searchTerms?: string[];
  suffix?: React.ReactNode;
};

type Props<TValue extends AllowedOptionValues = AllowedOptionValues> = {
  value?: TValue;
  onChange?: (value: TSelectOption<TValue> | null) => void;
  disabled?: boolean;
  options?: TSelectOption<TValue>[];
  triggerProps?: ButtonProps;
  disabledIcon?: React.ReactNode;
  emptyValueLabel?: React.ReactNode; // label that displays when no value selected
  asTrigger?: boolean; // only renders trigger without menu
  helperIcon?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  isRtl?: boolean;

  // external anchor
  anchorEl?: HTMLElement;
  hideTrigger?: boolean; // hides trigger and uses external anchor

  searchable?: boolean;
  searchFilter?: (options: TSelectOption<TValue>[], value: string) => TSelectOption<TValue>[];
  searchPlaceholder?: string;

  renderSelectedOption?: (value: TSelectOption<TValue>) => React.ReactNode;

  menuProps?: Partial<MenuProps>;
  menuWidth?: string;
};

export function Select<TValue extends AllowedOptionValues = AllowedOptionValues>({
  options,
  value,
  onChange,
  triggerProps,
  menuProps,
  menuWidth = 'auto',
  disabled,
  disabledIcon,
  emptyValueLabel,
  asTrigger,
  helperIcon,
  open: externalOpen,
  onOpenChange,
  isRtl,
  searchable,
  searchFilter,
  searchPlaceholder,
  renderSelectedOption,
  anchorEl: externalAnchorEl,
  hideTrigger,
}: Props<TValue>) {
  const { palette } = useTheme();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const open = externalOpen ?? internalOpen;
  const setOpen = (isOpen: boolean) => {
    setInternalOpen(isOpen);
    onOpenChange?.(isOpen);
  };

  const selectedOption = options.find((item) => item.value === value);
  const [searchValue, setSearchValue] = useState<string>('');

  const computeMenuWidth = useCallback(() => {
    if (menuWidth === 'fit-trigger') {
      if (anchorRef?.current?.offsetWidth) {
        return `${anchorRef.current.offsetWidth}px`;
      }

      return 'auto';
    }

    return menuWidth;
  }, [menuWidth]);

  const [menuWidthState, setMenuWidthState] = useState<string | null>(computeMenuWidth());

  const filteredOptions = useMemo(() => {
    if (!searchable) return options;

    if (searchFilter) return searchFilter(options, searchValue);

    return options.filter((option) => {
      const isSearchTermMatch = option.searchTerms?.some((term) => {
        return term.toLowerCase().includes(searchValue.toLowerCase());
      });

      return (
        isSearchTermMatch ||
        (typeof option.label === 'string' &&
          option.label.toLowerCase().includes(searchValue.toLowerCase()))
      );
    });
  }, [searchable, searchFilter, searchValue, options]);

  useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);

  useEffect(() => {
    const updateTriggerWidth = () => {
      const width = computeMenuWidth();

      setMenuWidthState(width);
    };

    updateTriggerWidth();

    // Update width when window resizes
    window.addEventListener('resize', updateTriggerWidth);

    return () => {
      window.removeEventListener('resize', updateTriggerWidth);
    };
  }, [computeMenuWidth]);

  let selectIcon;

  if (disabled && !!disabledIcon) {
    selectIcon = <IconRenderer icon={disabledIcon} />;
  } else if (helperIcon) {
    selectIcon = <IconRenderer icon={helperIcon} />;
  } else {
    selectIcon = (
      <i
        className="fa-regular fa-chevron-down"
        style={{
          fontSize: '16px',
          color: '#667085',
          transform: open ? 'rotate(180deg)' : undefined,
          transition: 'transform 0.3s ease-in-out',
        }}
      />
    );
  }

  return (
    <>
      {hideTrigger ? null : (
        <Button
          {...triggerProps}
          disabled={disabled}
          disableRipple
          ref={anchorRef}
          sx={{
            opacity: disabled ? '.5' : '1',
            transition: 'opacity 0.3s ease-in-out',
            p: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '8px',
            textTransform: 'none',
            border: '1px solid var(--Gray-300, #D0D5DD) !important',
            background: 'var(--White, #FFF) !important',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            color: '#344054',
            height: '44px',
            maxHeight: '44px',
            '&:hover': {
              color: '#344054',
            },
            ...triggerProps?.sx,
          }}
          onClick={(e) => {
            triggerProps?.onClick?.(e);
            setOpen(!open);
          }}
        >
          {selectedOption?.icon && (
            <Box
              sx={{
                minWidth: 0,
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconRenderer icon={selectedOption.icon} />
            </Box>
          )}

          <OptionLabel>
            {selectedOption
              ? renderSelectedOption?.(selectedOption) ?? selectedOption.label
              : value || (emptyValueLabel ?? 'Choose')}
          </OptionLabel>

          {selectIcon}
        </Button>
      )}

      <Menu
        disableEnforceFocus
        disableRestoreFocus
        autoFocus={!searchable}
        dir={isRtl ? 'rtl' : 'ltr'}
        elevation={0}
        open={asTrigger ? false : open}
        anchorEl={externalAnchorEl || anchorRef.current}
        onClose={() => setOpen(false)}
        {...menuProps}
        PaperProps={{
          ...menuProps?.PaperProps,
          sx: {
            ...menuProps?.PaperProps?.sx,
            '& .MuiMenu-list': {
              padding: 0,
            },
            mt: 1, // margin-top to move it down further
            background: 'var(--Gray-50, #F9FAFB) !important',
            borderRadius: '8px',
            border: '1px solid var(--Gray-300, #D0D5DD)',
            boxShadow: `0px 0px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)`,
            width: menuWidthState,
            overflow: 'auto',
            maxHeight: '300px',
            '&::-webkit-scrollbar': {
              width: '16px',
              backgroundColor: '#fff !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
              margin: '4px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#98A2B3',
              borderRadius: '8px',
              border: '6px solid transparent',
              backgroundClip: 'padding-box',
              minHeight: '30px',
            },
            '&::-webkit-scrollbar-thumb:active': {
              backgroundColor: '#98A2B3', // or theme.palette.grey[500]
            },
          },
        }}
      >
        {searchable && (
          <Box
            onClickCapture={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              placeholder={searchPlaceholder ?? 'Type to search'}
              value={searchValue}
              autoFocus
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-regular fa-search"
                      style={{ fontSize: '16px', color: palette.grey[500] }}
                    />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '0px',
                  backgroundColor: '#FFF',
                  height: 44,
                  padding: '0 16px !important',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none !important',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none !important',
                  },

                  '& input::placeholder': {
                    color: '#98A2B3',
                    opacity: 1,
                  },
                  '& input': {
                    height: 44, // Ensure input element matches container height
                    boxSizing: 'border-box', // Include padding in height calculation
                  },
                },
              }}
              // Styles for the root TextField component (less common for visual input styling)
              sx={{
                // Remove the default Mui-focused text color change if it exists
                '& .MuiInputBase-root.Mui-focused': {
                  color: 'inherit', // Keep default text color on focus
                },
              }}
            />
          </Box>
        )}

        {filteredOptions
          .filter((option) => {
            return !option.hidden;
          })
          .map((option) => (
            <MenuItem
              disableRipple
              disabled={option.disabled}
              sx={{
                p: '10px',
                display: 'flex',
                gap: '10px',
                pointerEvents: option.disabled ? 'none' : 'auto',
                opacity: option.disabled ? '.5' : '1',
                justifyContent: 'flex-start',
                backgroundColor: '#fff',
                height: '44px',
                maxHeight: '44px',

                '&.Mui-selected': {
                  backgroundColor: palette.grey[100],
                  '&:hover': {
                    backgroundColor: palette.grey[100],
                    color: '#000',
                  },
                },
                '&:hover': {
                  backgroundColor: palette.grey[50],
                  color: '#000',
                },
                '&.Mui-focusVisible': {
                  backgroundColor: palette.grey[100],
                  color: '#000',
                },
              }}
              key={option.value.toString()}
              selected={option.value === value}
              onClick={() => {
                onChange?.(option);
                setOpen(false);
              }}
            >
              {option.icon && (
                <Box
                  sx={{
                    minWidth: 0,
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconRenderer icon={option.icon} />
                </Box>
              )}

              <OptionLabel>{option.label}</OptionLabel>

              {option.suffix}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}
